import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Video, CheckCircle, XCircle, Circle, Square } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import ConfirmModal from '../components/ConfirmModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VistaCita({ user }) {
  const { expediente_id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [expediente, setExpediente] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [notas, setNotas] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);

  useEffect(() => {
    loadExpediente();
  }, [expediente_id]);

  const loadExpediente = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/operador/expediente/${expediente_id}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setExpediente(data.expediente);
        setChecklist(data.checklist);
        setNotas(data.expediente.notas_reunion || '');
      } else {
        toast.error('Error al cargar el expediente');
        navigate('/operador/panel');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
      navigate('/operador/panel');
    } finally {
      setLoading(false);
    }
  };

  const handleChecklistToggle = async (itemIndex, completado) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/operador/checklist/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          expediente_id,
          item_index: itemIndex,
          completado
        })
      });

      if (response.ok) {
        const updatedChecklist = { ...checklist };
        updatedChecklist.items[itemIndex].completado = completado;
        if (completado) {
          updatedChecklist.items[itemIndex].completado_en = new Date().toISOString();
        } else {
          updatedChecklist.items[itemIndex].completado_en = null;
        }
        setChecklist(updatedChecklist);
        toast.success('Checklist actualizado');
      } else {
        toast.error('Error al actualizar checklist');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    }
  };

  const handleUpdateStatus = async (nuevoEstado) => {
    setPendingStatus(nuevoEstado);
    setShowConfirmModal(true);
  };

  const confirmUpdateStatus = async () => {
    setShowConfirmModal(false);
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/operador/status/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          expediente_id,
          nuevo_estado: pendingStatus,
          notas
        })
      });

      if (response.ok) {
        toast.success('Estado actualizado exitosamente');
        setTimeout(() => navigate('/operador/panel'), 1500);
      } else {
        toast.error('Error al actualizar estado');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
      setPendingStatus(null);
    }
  };

  const startRecording = async () => {
    try {
      // Solicitar captura de pantalla con audio del sistema
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          mediaSource: 'screen',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      // Solicitar también audio del micrófono
      let micStream = null;
      try {
        micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 44100
          }
        });
      } catch (micError) {
        console.warn('No se pudo acceder al micrófono:', micError);
        toast.warning('Grabando sin micrófono (solo audio del sistema)');
      }

      // Combinar streams de audio
      let combinedStream = displayStream;
      
      if (micStream) {
        const audioContext = new AudioContext();
        const destination = audioContext.createMediaStreamDestination();
        
        // Audio de la pantalla
        const displayAudioTracks = displayStream.getAudioTracks();
        if (displayAudioTracks.length > 0) {
          const displaySource = audioContext.createMediaStreamSource(new MediaStream(displayAudioTracks));
          displaySource.connect(destination);
        }
        
        // Audio del micrófono
        const micSource = audioContext.createMediaStreamSource(micStream);
        micSource.connect(destination);
        
        // Combinar video de pantalla con audio mezclado
        const videoTrack = displayStream.getVideoTracks()[0];
        const audioTrack = destination.stream.getAudioTracks()[0];
        combinedStream = new MediaStream([videoTrack, audioTrack]);
      }

      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') 
        ? 'video/webm;codecs=vp9,opus' 
        : MediaRecorder.isTypeSupported('video/webm') 
        ? 'video/webm' 
        : 'video/mp4';
      
      mediaRecorderRef.current = new MediaRecorder(combinedStream, {
        mimeType,
        videoBitsPerSecond: 2500000,
        audioBitsPerSecond: 128000
      });

      recordedChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        await uploadRecordedVideo(blob);
        displayStream.getTracks().forEach(track => track.stop());
        if (micStream) {
          micStream.getTracks().forEach(track => track.stop());
        }
      };

      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      setRecordingTime(0);

      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      toast.success('Grabación iniciada con audio');

    } catch (error) {
      console.error('Error al iniciar grabación:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Debes permitir el acceso a la pantalla y audio para grabar');
      } else {
        toast.error('Error al iniciar grabación');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      
      toast.info('Procesando grabación...');
    }
  };

  const uploadRecordedVideo = async (blob) => {
    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('expediente_id', expediente_id);
      formData.append('video', blob, 'grabacion_reunion.webm');

      const response = await fetch(`${BACKEND_URL}/api/operador/video/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      // Leer body UNA SOLA VEZ - evita "Response body is already used"
      let data = null;
      try {
        data = await response.json();
      } catch (parseErr) {
        // Si no es JSON válido, crear objeto con el status
        data = { detail: `Error del servidor (HTTP ${response.status})` };
      }

      if (response.ok) {
        toast.success(data.message || 'Video subido exitosamente');
        await loadExpediente();
      } else {
        toast.error(data.detail || `Error al subir video (HTTP ${response.status})`);
      }
    } catch (error) {
      console.error('Error upload video:', error);
      toast.error('Error de conexión al subir grabación. Intenta nuevamente.');
    } finally {
      setUploadingVideo(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <div className="text-yellow-500 text-xl font-semibold">Cargando...</div>
        </div>
      </div>
    );
  }

  if (!expediente) return null;

  const { solicitante, cita, numero_expediente } = expediente;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <Button
          onClick={() => navigate('/operador/panel')}
          variant="outline"
          className="border-gray-700 text-gray-300 hover:bg-gray-800 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al Panel
        </Button>

        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Reunión con Cliente</h1>
          <p className="text-gray-400">Expediente: {numero_expediente} • Cliente: {solicitante.nombre_completo}</p>
        </div>
      </div>

      {/* Layout de 2 Columnas */}
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-6">
        {/* COLUMNA IZQUIERDA - Meet y Grabación */}
        <div className="space-y-6">
          {/* Google Meet - Compacto */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-6">
              {cita?.meet_link ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-green-500/10 border border-green-500/30 rounded-full flex items-center justify-center">
                      <Video className="w-6 h-6 text-green-500" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">Reunión Virtual</h3>
                      <p className="text-sm text-gray-400">Google Meet</p>
                    </div>
                  </div>
                  <a
                    href={cita.meet_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    Unirse a la Reunión
                  </a>
                </div>
              ) : (
                <p className="text-gray-400">No hay link de Google Meet disponible</p>
              )}
            </CardContent>
          </Card>

          {/* Grabación Automática con Instrucciones */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Video className="w-5 h-5 text-red-500" />
                Grabación de la Reunión
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {checklist?.video_url ? (
                <div className="space-y-4">
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                    <p className="text-green-500 text-sm font-medium">✅ Video guardado exitosamente</p>
                  </div>
                  <div className="bg-gray-900/50 rounded-lg overflow-hidden">
                    <video
                      controls
                      className="w-full"
                      src={checklist.video_url}
                    >
                      Tu navegador no soporta el elemento de video.
                    </video>
                  </div>
                  <a
                    href={checklist.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-400 hover:text-blue-300 underline"
                  >
                    Abrir video en nueva pestaña
                  </a>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Instrucciones */}
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                    <h4 className="text-blue-400 font-semibold text-sm mb-3">📝 Instrucciones de Grabación:</h4>
                    <ol className="text-sm text-gray-300 space-y-2 list-decimal list-inside">
                      <li>Haz clic en "Unirse a la Reunión" arriba</li>
                      <li>Vuelve a esta pestaña</li>
                      <li>Haz clic en "Iniciar Grabación" abajo</li>
                      <li>Selecciona la pestaña de Google Meet</li>
                      <li><strong className="text-yellow-500">Marca "Compartir audio de la pestaña"</strong></li>
                      <li>Permite acceso al micrófono cuando se solicite</li>
                      <li>Al terminar, haz clic en "Detener Grabación"</li>
                    </ol>
                  </div>

                  {/* Botón de Grabación */}
                  <div className="bg-gradient-to-br from-red-900/20 to-red-800/20 border border-red-500/30 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-white font-semibold">Grabación Automática</span>
                      {isRecording && (
                        <div className="flex items-center gap-2 bg-red-500/20 px-3 py-1.5 rounded-lg">
                          <Circle className="w-3 h-3 text-red-500 fill-red-500 animate-pulse" />
                          <span className="text-red-500 font-mono font-bold text-sm">{formatTime(recordingTime)}</span>
                        </div>
                      )}
                    </div>

                    {!isRecording ? (
                      <Button
                        onClick={startRecording}
                        disabled={uploadingVideo}
                        className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold h-11"
                      >
                        <Circle className="w-4 h-4 mr-2 fill-white" />
                        Iniciar Grabación
                      </Button>
                    ) : (
                      <Button
                        onClick={stopRecording}
                        disabled={uploadingVideo}
                        className="w-full bg-gray-700 hover:bg-gray-600 text-white font-semibold h-11"
                      >
                        <Square className="w-4 h-4 mr-2" />
                        Detener Grabación
                      </Button>
                    )}
                  </div>

                  {uploadingVideo && (
                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 text-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-500 mx-auto mb-2"></div>
                      <p className="text-yellow-500 text-sm font-medium">Subiendo video a Cloudinary...</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notas de Reunión */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Notas de la Reunión</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                placeholder="Escribe aquí tus notas sobre la reunión..."
                className="bg-gray-700/50 border-gray-600 text-gray-300 min-h-[150px]"
              />
            </CardContent>
          </Card>
        </div>

        {/* COLUMNA DERECHA - Datos + Checklist + Notas */}
        <div className="space-y-6">
          {/* Información del Cliente + Decisión Final */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-yellow-500" />
                Información del Cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Nombre Completo</p>
                  <p className="text-sm text-white font-medium">{solicitante.nombre_completo}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Cédula</p>
                  <p className="text-sm text-gray-300">{solicitante.cedula}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Email</p>
                  <p className="text-sm text-gray-300">{solicitante.email}</p>
                </div>
                {solicitante.telefono && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Teléfono</p>
                    <p className="text-sm text-gray-300">{solicitante.telefono}</p>
                  </div>
                )}
              </div>

              {solicitante.direccion && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Dirección</p>
                  <p className="text-sm text-gray-300">{solicitante.direccion}</p>
                </div>
              )}

              {/* Documentos */}
              {expediente.formulario_1_documentos && expediente.formulario_1_documentos.length > 0 && (
                <div className="pt-3 border-t border-gray-700">
                  <p className="text-xs text-gray-500 mb-2">Documentos</p>
                  <div className="grid grid-cols-2 gap-2">
                    {expediente.formulario_1_documentos.map((doc, idx) => (
                      <a
                        key={idx}
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-2 bg-gray-700/50 hover:bg-gray-700 rounded text-xs text-gray-300 hover:text-white transition-colors text-center"
                      >
                        📄 {doc.nombre}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Decisión Final */}
              <div className="pt-4 border-t border-gray-700">
                <p className="text-sm font-semibold text-white mb-3">Decisión Final</p>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handleUpdateStatus('cliente_retenido')}
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700 text-white h-12 text-sm font-semibold"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Cliente Retenido
                  </Button>
                  <Button
                    onClick={() => handleUpdateStatus('retiro_confirmado')}
                    disabled={saving}
                    className="bg-orange-600 hover:bg-orange-700 text-white h-12 text-sm font-semibold"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Retiro Confirmado
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Checklist */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Checklist de Reunión
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {checklist && checklist.items ? (
                  checklist.items.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 p-2 bg-gray-700/30 rounded-lg hover:bg-gray-700/50 transition-colors"
                    >
                      <Checkbox
                        id={`item-${idx}`}
                        checked={item.completado}
                        onCheckedChange={(checked) => handleChecklistToggle(idx, checked)}
                        className="border-gray-600"
                      />
                      <label
                        htmlFor={`item-${idx}`}
                        className={`flex-1 text-sm cursor-pointer ${
                          item.completado ? 'text-gray-500 line-through' : 'text-gray-300'
                        }`}
                      >
                        {item.nombre}
                      </label>
                      {item.completado && (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-400 text-sm">No hay checklist disponible</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal de Confirmación */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setPendingStatus(null);
        }}
        onConfirm={confirmUpdateStatus}
        title="Confirmar Decisión"
        message={`¿Estás seguro de marcar este expediente como "${pendingStatus === 'cliente_retenido' ? 'Cliente Retenido' : 'Retiro Confirmado'}"?`}
      />
    </div>
  );
}
